import { type RefObject, useEffect, useRef, useState } from 'react';

import type { CropMeta } from '@repo/ui/components/elements/Cropper/Cropper';
import { convertToWebp } from '@repo/ui/utils/image/convert-image-to-webp';

import type { ImageData } from './ImageInput';
import { buildCropCommitPayload, canCommitCrop, getOriginalUrl, useObjectUrl } from './image-input-utils';

interface UseImageInputArgs {
  value: ImageData | null;
  onChange: (imageData: ImageData | null) => void | Promise<void>;
  isDisabled: boolean;
}

interface UseImageInputResult {
  isModalOpen: boolean;
  inputRootRef: RefObject<HTMLDivElement | null>;
  src: string | undefined;
  previewUrl: string | null;
  initialCropMeta: CropMeta | undefined;
  uploadError: string | null;
  setUploadError: (error: string | null) => void;
  openCropper: (file: File) => Promise<void>;
  editImage: () => void;
  handleCropComplete: (thumbnailFile: File, meta: CropMeta) => Promise<void>;
  handleCloseModal: () => void;
  handleDelete: () => Promise<void>;
}

export function useImageInput({ value, onChange, isDisabled }: UseImageInputArgs): UseImageInputResult {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const inputRootRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef(false);
  const originalImageRef = useRef<File | null>(null);
  const operationInFlightRef = useRef(false);
  const [previewImage, setPreviewImage] = useState<File | null>(null);
  const [initialCropMeta, setInitialCropMeta] = useState<CropMeta | undefined>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const previewUrl = useObjectUrl(previewImage ?? undefined);
  const thumbnailUrl = useObjectUrl(value?.thumbnail);

  useEffect(() => {
    if (isModalOpen || !restoreFocusRef.current) return;

    restoreFocusRef.current = false;
    const animationFrame = requestAnimationFrame(() => {
      inputRootRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [isModalOpen]);

  const openCropper = async (file: File) => {
    if (isDisabled || operationInFlightRef.current) return;
    operationInFlightRef.current = true;
    setUploadError(null);
    try {
      const convertedFile = await convertToWebp(file);
      originalImageRef.current = convertedFile;
      setInitialCropMeta(undefined);
      setPreviewImage(convertedFile);
      setIsModalOpen(true);
    } catch {
      setUploadError('Could not process the selected image. Please try a different file.');
    } finally {
      operationInFlightRef.current = false;
    }
  };

  const editImage = () => {
    if (isDisabled) return;
    const originalUrl = value?.originalUrl;
    const originalFile = value?.original;
    setUploadError(null);
    if (originalFile) {
      originalImageRef.current = originalFile;
      setInitialCropMeta(value.meta);
      setPreviewImage(originalFile);
      setIsModalOpen(true);
      return;
    }
    if (originalUrl) {
      originalImageRef.current = null;
      setInitialCropMeta(value.meta);
      setPreviewImage(null);
      setIsModalOpen(true);
    }
  };

  const handleCropComplete = async (thumbnailFile: File, meta: CropMeta) => {
    if (isDisabled || operationInFlightRef.current) return;
    const originalImage = originalImageRef.current ?? value?.original;
    if (!canCommitCrop(originalImage, value)) {
      return;
    }

    operationInFlightRef.current = true;
    try {
      await onChange(buildCropCommitPayload(originalImage, thumbnailFile, meta, value));
      setUploadError(null);
      setIsModalOpen(false);
      originalImageRef.current = null;
      setPreviewImage(thumbnailFile);
    } catch (error) {
      const saveError = error instanceof Error ? error : new Error('Could not save the image. Please try again.');
      setUploadError(saveError.message);
      throw saveError;
    } finally {
      operationInFlightRef.current = false;
    }
  };

  const handleCloseModal = () => {
    restoreFocusRef.current = true;
    setIsModalOpen(false);
    originalImageRef.current = null;
    setInitialCropMeta(undefined);
    setPreviewImage(null);
  };

  const handleDelete = async () => {
    if (isDisabled || operationInFlightRef.current) return;
    operationInFlightRef.current = true;
    try {
      await onChange(null);
      setPreviewImage(null);
      setUploadError(null);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Could not remove the image. Please try again.');
    } finally {
      operationInFlightRef.current = false;
    }
  };

  const src = previewUrl || thumbnailUrl || getOriginalUrl(value);

  return {
    isModalOpen,
    inputRootRef,
    src,
    previewUrl,
    initialCropMeta,
    uploadError,
    setUploadError,
    openCropper,
    editImage,
    handleCropComplete,
    handleCloseModal,
    handleDelete,
  };
}
